<template>
<div class="position-relative overflow-hidden" :dir="isHebrew ? 'rtl' : 'ltr'">
    <NavBar/>
    <div>
      <img
        alt=""
        class="background-image"
        src="https://firebasestorage.googleapis.com/v0/b/tripplanner-308b3.appspot.com/o/page_images%2Fcircles-background.png?alt=media&token=d420088e-66c6-4c95-bd3b-2f218dd36aba">
    </div>
    <ImageBanner :backgroundImage= "'https://firebasestorage.googleapis.com/v0/b/tripplanner-308b3.appspot.com/o/page_images%2Fride-info-banner.jpg?alt=media&token=b765e21f-2604-4874-a029-3453357e4897'"
                 :paragraph="t('editRideAccessBannerParagraph')"
                 size="240px"
                 :title="t('editRideAccessBannerTitle')"/>
  <b-container>
    <b-row class="justify-content-center">
      <b-col col md="6">
        <div class="video-tutorials-section share-ride-tutorial mb-4">
          <div class="video-tutorials-title">
            <span class="icon">🎥</span> {{ t('shareRideTutorialsRecommendedTitle') }}
          </div>
          <div class="video-tutorials-list">
            <div class="video-tutorial-item">
              <div
                class="video-tutorial-card"
                @click="toggleShareRideVideo"
              >
                <div class="video-tutorial-header">
                  <div class="video-tutorial-title">{{ t('shareRideTutorialVideoTitle') }}</div>
                  <span class="video-tutorial-arrow" :class="{ 'is-open': showShareRideVideo }">▶</span>
                </div>
              </div>
              <transition name="slide-down">
                <div v-if="showShareRideVideo" class="video-container-wrapper">
                  <div class="tutorial-video-container">
                    <video
                      v-if="shareRideTutorialVideo.videoUrl && !shareRideTutorialVideo.error"
                      ref="shareRideVideo"
                      :src="shareRideTutorialVideo.videoUrl"
                      controls
                      class="tutorial-video"
                      @loadstart="onShareRideVideoLoadStart"
                      @error="onShareRideVideoError"
                      @loadeddata="onShareRideVideoLoaded"
                      preload="none"
                    >
                      {{ t('videoTagNotSupported') }}
                    </video>
                    <div v-else-if="shareRideTutorialVideo.loading" class="video-loading">
                      <div class="spinner-border text-primary" role="status">
                        <span class="sr-only">{{ t('loading') }}</span>
                      </div>
                      <p>{{ t('attractionEditingLoadingVideo') }}</p>
                    </div>
                    <div v-else-if="shareRideTutorialVideo.error" class="video-error">
                      <p>{{ t('attractionEditingVideoLoadError') }}</p>
                      <button type="button" class="retry-button" @click="retryShareRideVideo">{{ t('attractionEditingRetryVideo') }}</button>
                    </div>
                  </div>
                </div>
              </transition>
            </div>
          </div>
        </div>
        <!-- Updated-terms gate: a ride manager who has not signed the current
             business agreement cannot share (link, WhatsApp, PDF) until signing. -->
        <b-card v-if="mustSignUpdatedTerms" class="mb-5" bg-variant="light" border-variant="warning">
          <div :class="isHebrew ? 'text-right' : 'text-left'">
            <h5 class="mb-2">{{ t('updatedTermsShareGateTitle') }}</h5>
            <p class="mb-3">{{ t('updatedTermsShareGateBody') }}</p>
            <b-button variant="success" class="w-100" @click="showModal('updatedTerms')">
              {{ t('updatedTermsShareGateButton') }}
            </b-button>
            <b-button variant="outline-secondary" class="w-100 mt-2" @click="returnToRide">
              {{ t('editRideAccessBackWithoutSave') }}
            </b-button>
          </div>
        </b-card>
        <template v-else>
        <b-form>
          <!-- Prepaid billing gate -->
          <div v-if="isPrepaid && !isApprovedForSharing" class="mb-3">
            <b-card class="mb-2" bg-variant="light" border-variant="primary">
              <div :class="isHebrew ? 'text-right' : 'text-left'">
                <h5 class="mb-1">{{ t('editRideAccessPrepaidApproveShareTitle') }}</h5>
                <div v-if="ridePriceBreakdown" class="mb-2">
                  <div>{{ t('editRideAccessPrepaidTripDaysLine', { count: chargedDays }) }}</div>
                  <div><strong>{{ t('editRideAccessPrepaidShareCostLine', { amount: totalAmountFormatted }) }}</strong></div>
                  <div>{{ t('editRideAccessPrepaidPerDayLine', { price: dailyPriceFormatted }) }}</div>
                </div>
                <div v-if="isFirstShareOnUs" class="mb-3 text-success font-weight-bold">
                  {{ t('editRideAccessPrepaidFirstShareLine1') }}<br>
                  {{ t('editRideAccessPrepaidFirstShareLine2') }}
                </div>
                <div v-if="!canApproveSharing" class="mb-3 text-danger font-weight-bold">
                  {{ t('editRideAccessPrepaidInsufficientBalance') }}
                </div>

                <b-button
                  v-if="!isApprovedForSharing"
                  variant="success"
                  class="w-100"
                  :disabled="approval.loading || (ridePriceBreakdown && ridePriceBreakdown.already_paid) || !canApproveSharing"
                  @click="onPrepaidPrimaryClick"
                >
                  <span v-if="!approval.loading">
                    {{
                      businessBasicAgreementAccepted === true
                        ? t('editRideAccessPrepaidApproveShareButton')
                        : t('editRideAccessPrepaidContinueTermsButton')
                    }}
                  </span>
                  <span v-else>
                    <span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                    {{ t('loading') }}
                  </span>
                </b-button>
                <b-button
                  v-if="!isApprovedForSharing"
                  variant="outline-secondary"
                  class="w-100 mt-2"
                  @click="returnToRide"
                >
                  {{ t('editRideAccessPrepaidCancelButton') }}
                </b-button>
                <div v-if="!canApproveSharing" class="mt-2">
                  <b-button variant="success" class="w-100 mb-2 setup-billing-btn" @click="openInsufficientBalancePopup">
                    <div class="setup-billing-btn__main">
                      <i class="fas fa-credit-card setup-billing-btn__icon"></i>{{ t('editRideAccessPrepaidTopUpButton') }}
                    </div>
                    <div class="setup-billing-btn__sub">{{ t('editRideAccessPrepaidTopUpButtonSub') }}</div>
                  </b-button>
                  <b-button variant="primary" class="w-100" @click="openWhatsAppSupport">
                    {{ t('editRideAccessPrepaidWhatsAppButton') }}
                  </b-button>
                </div>
              </div>
            </b-card>
          </div>

          <!-- Save card banner shown after first free share -->
          <div v-if="showSaveCardBanner" class="mb-3">
            <b-alert
              show
              variant="warning"
              dismissible
              class="save-card-banner"
              :dir="isHebrew ? 'rtl' : 'ltr'"
              :class="isHebrew ? 'text-right' : 'text-left'"
              @dismissed="showSaveCardBanner = false"
            >
              <strong>{{ t('editRideAccessSaveCardBannerTitle') }}</strong><br>
              {{ t('editRideAccessSaveCardBannerText') }}
              <div class="mt-2">
                <b-button variant="warning" size="sm" @click="openInsufficientBalancePopup">
                  {{ t('editRideAccessPrepaidTopUpButton') }}
                </b-button>
              </div>
            </b-alert>
          </div>

          <b-form-group
            v-if="isUserSubdomainManager&&rideIsWhiteLabel"
            id="label-details"
            :class="['d-flex align-items-center justify-content-end', isHebrew ? 'text-right flex-row-reverse' : 'text-left flex-row-reverse']"
            label-for="checkbox-1">
            <template #label>
              <span>{{ t('editRideAccessAllowEditLabel') }}</span>
              <b-button
                v-b-popover.click.top.html="editRideHelpText"
                :title="t('rideInfoMoreInfo')"
                variant="link"
                :class="['p-0 help-icon-btn', isHebrew ? 'mr-2' : 'ml-2']"
                style="vertical-align: middle;"
              >
                <i class="fas fa-question-circle" style="color: #007bff; font-size: 1.1em;"></i>
              </b-button>
            </template>
          <b-form-checkbox
              id="checkbox-1"
              class="checkbox-right ml-2 checkbox-custom mb-2"
              name="checkbox-1"
              size="lg"
              v-model="localRideAccesskeyActionIsEdit"
              :unchecked-value=false
              :value=true>
          </b-form-checkbox>
          </b-form-group>
          <b-alert
            v-if="!rideAccesskeyIsActive"
            show
            variant="warning"
            :class="isHebrew ? 'text-right' : 'text-left'"
          >
            {{ t('editRideAccessLinkBlockedWarning') }}
            <b-button size="sm" variant="warning" class="mt-2 d-block" @click="onRestoreLink">
              {{ t('editRideAccessRestoreLinkButton') }}
            </b-button>
          </b-alert>

          <b-form-group
              v-if="(!isPrepaid || isApprovedForSharing)"
              id="label-title"
              :class="isHebrew ? 'text-right' : 'text-left'"
              :label="t('editRideAccessRegistrationLinkLabel')"
              label-for="title">
            <b-form-input
                v-model="localRideAccesskeySecretKey"
                id="title"
                :class="['form-control border-primary rounded-2 py-4', isHebrew ? 'text-right' : 'text-left']"
                placeholder=""
                required
                type="text"
                readonly>
            </b-form-input>
            <b-button @click="copyLink" variant="primary" class="mt-2">
              {{ t('editRideAccessCopyLinkButton') }}
            </b-button>
          </b-form-group>
          <!-- Same gate as the registration link and the PDF button above: a
               prepaid manager who has not paid for sharing this trip must not be
               offered a way to let travellers in, because every one of them is
               billed while the upfront share payment never happened. -->
          <div class="whatsapp-invite-panel"
               :class="isHebrew ? 'text-right' : 'text-left'"
               v-if="whatsappInviteAllowed && whatsappInvite.invite_url">
            <h3 class="whatsapp-invite-title">{{ t('whatsappInviteTitle') }}</h3>
            <p class="whatsapp-invite-explain">{{ t('whatsappInviteExplain') }}</p>

            <b-alert
              show
              variant="warning"
              class="whatsapp-invite-billing-notice"
              :class="isHebrew ? 'text-right' : 'text-left'"
            >
              <!-- One heading, then two separate warnings: the first is about
                   money, the second about who can see the trip. Each is its own
                   key so either can be reworded without disturbing the other. -->
              <div class="whatsapp-invite-notice-title">
                <span class="whatsapp-invite-billing-icon">⚠️</span>{{ t('whatsappInviteNoticeTitle') }}
              </div>
              <ul class="whatsapp-invite-notice-list">
                <li>{{ t('whatsappInviteBillingNotice') }}</li>
                <li>{{ t('whatsappInviteAccessNotice') }}</li>
                <li>{{ t('whatsappInvitePricingNotice') }}</li>
              </ul>
            </b-alert>

            <input
              class="form-control"
              type="text"
              readonly
              :value="whatsappInvite.invite_url"
              :dir="'ltr'"
            />
            <button class="btn btn-success whatsapp-invite-copy" @click="copyWhatsappInvite">
              {{ t('whatsappInviteCopy') }}
            </button>

            <div class="whatsapp-invite-meta">
              <span>{{ t('whatsappInviteCode') }}: <code>{{ whatsappInvite.join_code }}</code></span>
              <span>
                {{ t('whatsappInviteRegistered') }}:
                {{ whatsappInvite.registered_count }}
                <template v-if="whatsappInvite.authorized_users_count">
                  / {{ whatsappInvite.authorized_users_count }}
                </template>
                <template v-else>({{ t('whatsappInviteUnlimited') }})</template>
              </span>
            </div>

            <!-- Day-before reminder settings. The message body is a preview and
                 deliberately read-only: a message sent outside the 24-hour
                 window must be a Meta-approved template, whose wording cannot
                 be edited per manager. -->
            <div class="whatsapp-reminder">
              <h4 class="whatsapp-reminder-title">{{ t('whatsappReminderTitle') }}</h4>
              <p class="whatsapp-reminder-explain">{{ t('whatsappReminderExplain') }}</p>

              <b-form-checkbox v-model="reminderSettings.enabled" class="mb-2">
                {{ t('whatsappReminderEnable') }}
              </b-form-checkbox>

              <!-- The nightly itinerary: a SEPARATE opt-in, not a sub-option of
                   the reminder above. It sends once per traveller per trip DAY
                   rather than once per trip, so the manager is agreeing to a
                   different order of magnitude of cost and the label says so
                   before they tick it. -->
              <b-form-checkbox v-model="reminderSettings.daily_itinerary_enabled">
                {{ t('whatsappDailyItineraryEnable') }}
              </b-form-checkbox>
              <p class="whatsapp-reminder-sub">{{ t('whatsappDailyItineraryExplain') }}</p>

              <!-- Hidden rather than disabled while both are off: when nothing
                   will be sent, a send hour and a timezone are answers to
                   questions nobody asked. The checkboxes and Save stay so the
                   OFF state can be saved. Shown for EITHER message type, since
                   both send at this hour in this zone. -->
              <template v-if="anyProactiveMessageEnabled">
                <div class="whatsapp-reminder-row">
                  <label>{{ t('whatsappReminderHour') }}</label>
                  <b-form-select
                    v-model="reminderSettings.hour"
                    :options="reminderHourOptions"
                  />
                </div>

                <div class="whatsapp-reminder-row">
                  <label>{{ t('whatsappReminderTimezone') }}</label>
                  <b-form-select
                    v-model="reminderSettings.timezone"
                    :options="timezoneOptions"
                  />
                </div>
              </template>

              <!-- Examples last: all decisions (checkboxes) and the shared
                   timing controls above come before either sample message.
                   Each preview is collapsed behind its own toggle with its
                   own state, so opening one never opens the other -- mirrors
                   the ▶ arrow idiom used by the video-tutorial section near
                   the top of this file. -->
              <template v-if="reminderSettings.enabled">
                <template v-if="reminderSettings.template_available">
                  <div
                    class="whatsapp-reminder-preview-toggle"
                    @click="showReminderPreview = !showReminderPreview"
                  >
                    <span>{{ t('whatsappReminderPreviewToggle') }}</span>
                    <span class="whatsapp-reminder-preview-arrow" :class="{ 'is-open': showReminderPreview }">▶</span>
                  </div>
                  <div class="whatsapp-reminder-preview" v-if="showReminderPreview">
                    <div class="whatsapp-reminder-preview-label">{{ t('whatsappReminderPreview') }}</div>
                    <div class="whatsapp-reminder-preview-body" :dir="isHebrew ? 'rtl' : 'ltr'">{{ reminderPreview }}</div>
                    <div class="whatsapp-reminder-preview-note">{{ t('whatsappReminderPreviewNote') }}</div>
                  </div>
                </template>
                <!-- A warning, not an example: always visible, never collapsed. -->
                <div class="whatsapp-reminder-no-template" v-else>
                  {{ t('whatsappReminderNoTemplate') }}
                </div>
              </template>

              <template v-if="reminderSettings.daily_itinerary_enabled">
                <div
                  class="whatsapp-reminder-preview-toggle"
                  @click="showDailyItineraryPreview = !showDailyItineraryPreview"
                >
                  <span>{{ t('whatsappDailyItineraryPreviewToggle') }}</span>
                  <span class="whatsapp-reminder-preview-arrow" :class="{ 'is-open': showDailyItineraryPreview }">▶</span>
                </div>
                <div class="whatsapp-reminder-preview" v-if="showDailyItineraryPreview">
                  <div class="whatsapp-reminder-preview-label">{{ t('whatsappDailyItineraryPreview') }}</div>
                  <div class="whatsapp-reminder-preview-body" :dir="isHebrew ? 'rtl' : 'ltr'">{{ dailyItineraryPreview }}</div>
                  <div class="whatsapp-reminder-preview-note">{{ t('whatsappDailyItineraryPreviewNote') }}</div>
                </div>
              </template>

              <p v-if="reminderError" class="whatsapp-reminder-error">{{ reminderError }}</p>
            </div>
          </div>
          <div class="whatsapp-invite-panel"
               :class="isHebrew ? 'text-right' : 'text-left'"
               v-else-if="whatsappInviteAllowed && !whatsappInviteError && whatsappInvite.can_generate">
            <h3 class="whatsapp-invite-title">{{ t('whatsappInviteTitle') }}</h3>
            <p class="whatsapp-invite-explain">{{ t('whatsappInviteNotCreatedYet') }}</p>
            <b-button
              variant="success"
              :disabled="generatingInvite"
              @click="generateWhatsappInvite"
            >
              {{ t('whatsappInviteGenerateButton') }}
            </b-button>
          </div>
          <p v-else-if="whatsappInviteAllowed && whatsappInviteError" class="whatsapp-invite-error">
            {{ whatsappInviteError }}
          </p>
          <div
            v-if="isUserSubdomainManager&&rideIsWhiteLabel"
            :class="isHebrew ? 'text-right' : 'text-left'"
          >
            <b-button @click="goToGetSingleAccessKey" variant="primary" class="mt-2 mb-2">
              {{ t('editRideAccessSingleUseLinkButton') }}
            </b-button>
          </div>
          <b-form-group
              id="label-title"
              :class="isHebrew ? 'text-right' : 'text-left'"
              :label="t('editRideAccessRedirectLabel')"
              label-for="title">
            <b-form-input
                v-model="localRideAccesskeyRedirectPage"
                id="label-RedirectPage"
                :class="['form-control border-primary rounded-2 py-4', isHebrew ? 'text-right' : 'text-left']"
                :placeholder="t('editRideAccessRedirectPlaceholder')"
                required
                type="text">
            </b-form-input>
          </b-form-group>
          <b-form-group
              id="label-title"
              :class="isHebrew ? 'text-right' : 'text-left'"
              :label="t('editRideAccessAuthorizedUsersLabel')"
              label-for="title">
            <b-form-input
                v-model="localrideAccesskeyAuthorizedUsersCount"
                id="label-Count"
                :class="['form-control border-primary rounded-2 py-4', isHebrew ? 'text-right' : 'text-left']"
                placeholder="4"
                required
                type="text">
            </b-form-input>
            <h6>{{ t('editRideAccessAuthorizedUsersHint') }}</h6>
          </b-form-group>
        </b-form>

        <!-- Export PDF Button -->
        <div v-if="!isPrepaid || isApprovedForSharing" :class="isHebrew ? 'text-right' : 'text-left'">
          <b-button
            @click="openPdfStyleChooser"
            variant="primary"
            class="mt-2"
            :disabled="isGeneratingPDF"
          >
            <span v-if="!isGeneratingPDF">
              <i class="fas fa-file-pdf"></i> {{ t('exportPDF') }}
            </span>
            <span v-else>
              <span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
              {{ t('generatingPDF') }}
            </span>
          </b-button>

          <!-- Which PDF: the practical itinerary or the marketing brochure -->
          <b-modal
            id="pdfStyleModal"
            :title="t('pdfStyleChooserTitle')"
            hide-footer
            :dir="isHebrew ? 'rtl' : 'ltr'"
          >
            <div :class="isHebrew ? 'text-right' : 'text-left'">
              <b-button
                variant="outline-primary"
                block
                class="mb-3 py-3"
                @click="exportToPDF('itinerary')"
              >
                <div class="font-weight-bold">
                  <i class="fas fa-list-ul"></i> {{ t('pdfStyleItineraryLabel') }}
                </div>
                <small class="text-muted">{{ t('pdfStyleItineraryHint') }}</small>
              </b-button>

              <b-button
                variant="outline-primary"
                block
                class="py-3"
                @click="openBrochurePhotoPicker"
              >
                <div class="font-weight-bold">
                  <i class="fas fa-images"></i> {{ t('pdfStyleBrochureLabel') }}
                </div>
                <small class="text-muted">{{ t('pdfStyleBrochureHint') }}</small>
              </b-button>
            </div>
          </b-modal>

          <BrochurePhotoPicker
            ref="brochurePhotoPicker"
            :ride-id="$route.params.ride_key"
            @generate="photos => exportToPDF('brochure', photos)"
          />
        </div>

        <!-- Allow Sale Via API Checkbox -->
        <b-form-group
          id="label-allow-sale-api"
          :class="['d-flex align-items-center mt-3 justify-content-end', isHebrew ? 'text-right flex-row-reverse' : 'text-left flex-row-reverse']"
          label-for="checkbox-allow-sale-api">
          <template #label>
            <span>{{ t('editRideAccessAllowSaleLabel') }}</span>
            <b-button
              v-b-popover.click.top.html="helpText"
              :title="t('rideInfoMoreInfo')"
              variant="link"
              :class="['p-0 help-icon-btn', isHebrew ? 'mr-2' : 'ml-2']"
              style="vertical-align: middle;"
            >
              <i class="fas fa-question-circle" style="color: #007bff; font-size: 1.1em;"></i>
            </b-button>
            
          </template>
          <b-form-checkbox
            id="checkbox-allow-sale-api"
            class="checkbox-right checkbox-custom mb-2"
            name="checkbox-allow-sale-api"
            size="lg"
            v-model="localAllowSaleViaLocalShop"
            :unchecked-value=false
            :value=true>
          </b-form-checkbox>
        </b-form-group>

        <PrimaryButton
          class="w-100 mb-5"
          @click="this.saveRideAccessKeyDetailes"
        >
          {{ t('editRideAccessSaveButton') }} 
        </PrimaryButton>
        <SecondaryButton
          :callBack="returnToRide"
          class="w-100 mb-5"
        >
          {{ t('editRideAccessBackWithoutSave') }}
        </SecondaryButton>
        </template>
      </b-col>
    </b-row>
  </b-container>
  <b-container>
    <b-row class="justify-content-center">
      <b-col col md="6">
        <RideConnectedUsers :rideId="$route.params.ride_key" />
      </b-col>
    </b-row>
  </b-container>
  <b-container v-if="rideAccesskeyActionIsEdit">
    <b-row class="justify-content-center">
      <b-col col md="6">
        <RideCopiedUsers :rideId="$route.params.ride_key" />
      </b-col>
    </b-row>
  </b-container>
  <AppFooter/>
</div>
</template>

<script>
import ImageBanner from "@/components/ImageBanner/ImageBanner.vue";
import PrimaryButton from "@/components/Buttons/PrimaryButton.vue";
import NavBar from "@/components/NavBar.vue";
import AppFooter from "@/components/Template/AppFooter.vue";
import SecondaryButton from "@/components/Buttons/SecondaryButton.vue";
import RideConnectedUsers from '@/components/RideConnectedUsers.vue';
import RideCopiedUsers from '@/components/RideCopiedUsers.vue';
import BrochurePhotoPicker from '@/components/Modals/BrochurePhotoPicker.vue';
import { mapActions, mapGetters } from 'vuex';
import { translationMixin } from "@/mixins/translationMixin";
import whatsappInviteApi from '@/api/whatsappInvite';
import { TIMEZONE_GROUPS, offsetLabel } from '@/common/timezones';

export default {
  name: 'EditRideAccessKey',
  mixins: [translationMixin],
  components: {
    AppFooter,
    BrochurePhotoPicker,
    PrimaryButton,
    ImageBanner,
    NavBar,
    SecondaryButton,
    RideConnectedUsers,
    RideCopiedUsers,
    },
  computed: {...mapGetters([  'rideInformationTitle',
                              'rideAccesskeySecretKey',
                              'rideAccesskeyRedirectPage',
                              'rideAccesskeyAuthorizedUsersCount',
                              'userId',
                              'rideAccesskeyAction',
                              'rideAccesskeyActionIsEdit',
                              'isUserSubdomainManager',
                              'rideIsWhiteLabel',
                              'allowSaleViaLocalShop',
                              'isPrepaid',
                              'prepaidBalance',
                              'ridePriceBreakdown',
                              'approval',
                              'isApprovedForSharing',
                              'BisnessBasicAgreementShareGateCompletedNonce',
                              'BisnessBasicAgreementShareGateArmed',
                              'businessBasicAgreementAccepted',
                              'rideAccesskeyIsActive',
                              'apptoken',
                              'mustSignUpdatedTerms',
                              ]),
      
      localRideAccesskeyActionIsEdit:{
        get () {
          return this.rideAccesskeyActionIsEdit;
        },
        set (value) {
          const convertedValue = value ;
          this.$store.commit('setRideAccesskeyAction', convertedValue)
        },
      },
      localRideAccesskeySecretKey:{
        get () {
          return this.rideAccesskeySecretKey
        },
        set (value) {
          this.$store.commit('setRideAccesskeySecretKey', value )
        }
      },
      localRideAccesskeyRedirectPage:{
        get () {
          return this.rideAccesskeyRedirectPage
        },
        set (value) {
          this.$store.commit('setRideAccesskeyRedirectPage', value )
        }
      },
      localrideAccesskeyAuthorizedUsersCount:{
        get () {
          return this.rideAccesskeyAuthorizedUsersCount
        },
        set (value) {
          this.$store.commit('setRideAccesskeyAuthorizedUsersCount', value )
        }
      },
      localAllowSaleViaLocalShop:{
        get () {
          return this.allowSaleViaLocalShop
        },
        set (value) {
          this.$store.commit('setAllowSaleViaLocalShop', value )
        }
      },
      // Literally the gate the registration link and the PDF button use. The
      // WhatsApp panel offers a way for travellers to join, and every joiner is
      // billed (record_registration_billing_transaction) -- so a prepaid manager
      // who has not paid for sharing this trip must not see it, or they would be
      // charged per traveller without the upfront share payment that deducts
      // prepaid_balance ever happening. The backend refuses to mint too; this is
      // the UI half of that, not the whole guard.
      whatsappInviteAllowed() {
        // WhatsApp is for private rides only (owner, 2026-10-18). The server
        // is the authority: it answers ride_not_private for a public ride,
        // and loadWhatsappInvite records that here.
        if ( this.whatsappRideNotPrivate ) return false;
        return !this.isPrepaid || this.isApprovedForSharing;
      },
      helpText() {
        return this.t('editRideAccessAllowSaleHelp')
      },
      editRideHelpText() {
        return this.t('editRideAccessEditRideHelp')
      },
      priceCurrency() {
        return (this.ridePriceBreakdown && this.ridePriceBreakdown.currency) ? this.ridePriceBreakdown.currency : 'ILS';
      },
      chargedDays() {
        return this.ridePriceBreakdown && this.ridePriceBreakdown.charged_days ? this.ridePriceBreakdown.charged_days : 0;
      },
      totalAmountNumber() {
        return Number((this.ridePriceBreakdown && this.ridePriceBreakdown.total_amount) || 0);
      },
      dailyPriceNumber() {
        return Number((this.ridePriceBreakdown && this.ridePriceBreakdown.daily_price) || 0);
      },
      prepaidBalanceNumber() {
        return Number(this.prepaidBalance || 0);
      },
      hasEnoughBalance() {
        return this.prepaidBalanceNumber >= this.totalAmountNumber;
      },
      startingCredit () {
        return this.$store.state.regionConfig.prices.firstShareStartingBalance
      },
      canApproveSharing() {
        return this.hasEnoughBalance || this.prepaidBalanceNumber == this.startingCredit;
      },
      isFirstShareOnUs() {
        return this.prepaidBalanceNumber == this.startingCredit;
      },
      currencySymbol () {
        return this.$store.state.regionConfig.currencySymbol
      },
      isUsd () {
        return this.$store.state.regionConfig.currency === 'USD'
      },
      totalAmountFormatted() {
        return this.isUsd
          ? '$' + this.totalAmountNumber
          : this.totalAmountNumber + ' ' + this.currencySymbol;
      },
      dailyPriceFormatted() {
        return this.isUsd
          ? '$' + this.dailyPriceNumber
          : this.dailyPriceNumber + ' ' + this.currencySymbol;
      },
      timezoneOptions() {
        // The default follows the travellers: each day is timed in the zone of
        // that day's own activities. The zone below is therefore an EXAMPLE
        // from day one, not a statement about the whole trip -- on a Rome ->
        // Bangkok trip the later days resolve elsewhere.
        const derived = this.reminderSettings.resolved_timezone;
        const derivedLabel = derived
          ? `${this.t( 'whatsappReminderTimezoneAuto' )} (${
              this.t( 'whatsappReminderTimezoneAutoExample' )
                  .replace( '{zone}', this.zoneLabel( derived ) )})`
          : this.t( 'whatsappReminderTimezoneAuto' );

        const options = [ { value: '', text: derivedLabel } ];
        TIMEZONE_GROUPS.forEach( ( group ) => {
          options.push( {
            label: this.t( `whatsappReminderTimezoneGroup_${group.key}` ),
            options: group.zones.map( ( zone ) => ( {
              value: zone.value,
              text: this.zoneLabel( zone.value ),
            } ) ),
          } );
        } );
        return options;
      },
      anyProactiveMessageEnabled() {
        // The hour and timezone are shared by both message types, so they are
        // relevant as soon as EITHER is on. Written as a computed rather than
        // an inline || so the two checkboxes stay visibly independent: neither
        // gates the other's controls.
        return Boolean( this.reminderSettings.enabled )
          || Boolean( this.reminderSettings.daily_itinerary_enabled );
      },
      reminderHourOptions() {
        return Array.from( { length: 24 }, ( unused, hour ) => ( {
          value: hour,
          text: `${String( hour ).padStart( 2, '0' )}:00`,
        } ) );
      },
      reminderPreview() {
        // Mirrors the approved template body exactly. If Meta's wording ever
        // changes, this string and translationMixin must change with it.
        return this.t( 'whatsappReminderPreviewBody' )
          .replace( '{name}', 'דנה' )
          .replace( '{title}', this.rideTitleForPreview )
          .replace( '{link}', 'https://tripplanner.co.il/wa_login/...' );
      },
      rideTitleForPreview() {
        // Read from the reminder-settings response, not the store: this page
        // never calls loadRideInformation, so rideInformationTitle (used
        // above for the PDF export) is empty on a direct visit. The settings
        // endpoint is also the server that will actually fill {{2}} in the
        // template, so this shows travellers' real preview instead of a
        // client-side guess.
        return this.reminderSettings.ride_title || '—';
      },
      dailyItineraryPreview() {
        // Copy of the approved nightly-itinerary template body, with sample
        // values filled in for the traveller name, date, and each activity's
        // time/title/navigation link. This text lives at Meta, not here -- if
        // the approved wording ever changes there, this string and
        // translationMixin must change with it, or the manager is shown a
        // preview travellers no longer receive.
        return this.t( 'whatsappDailyItineraryPreviewBody' );
      },
    },
  watch: {
    // Signed in the updated-terms popup on this page: load what the gate held back.
    mustSignUpdatedTerms(blocked, wasBlocked) {
      if (wasBlocked && !blocked) {
        this.loadShareInfo();
      }
    },
    BisnessBasicAgreementShareGateCompletedNonce(newVal, oldVal) {
      if (newVal !== oldVal && newVal > 0 && this.BisnessBasicAgreementShareGateArmed) {
        this.$store.commit('setBisnessBasicAgreementShareGateArmed', false);
        this.approveAndLoadShareInfo();
      }
    },
  },
  data() {
    return {
      isGeneratingPDF: false,
      showShareRideVideo: false,
      showSaveCardBanner: false,
      shareRideTutorialVideo: {
        videoUrl: 'https://firebasestorage.googleapis.com/v0/b/tripplanner-308b3.appspot.com/o/tutorials%2Fshering_the_Ride_5.mp4?alt=media&token=fe56a71b-d9bb-4116-91ca-8997ceb67581',
        loading: false,
        error: false,
      },
      whatsappInvite: { invite_url: '', join_code: '', registered_count: 0, authorized_users_count: 0, can_generate: false },
      whatsappInviteError: '',
      whatsappRideNotPrivate: false,
      generatingInvite: false,
      // template_available defaults to true so an older backend that has not
      // deployed FIX 4 yet -- and so never sends this field -- keeps showing
      // the preview it always showed, rather than hiding it for everyone.
      reminderSettings: {
        enabled: false, hour: 18, timezone: '', resolved_timezone: '',
        ride_title: '', template_available: true,
        // The nightly itinerary, a separate opt-in. Both default to the safe
        // answer for a backend that does not send them yet: not enabled, and
        // not live -- so an older server never makes the page promise a send
        // that nothing performs.
        daily_itinerary_enabled: false, daily_itinerary_live: false,
      },
      reminderError: '',
      // Each example message is collapsed behind its own toggle, independent
      // of the other -- opening the day-before preview must not open the
      // nightly one, and vice versa.
      showReminderPreview: false,
      showDailyItineraryPreview: false,
    }
  },
    methods:
    {
    ...mapActions([ 'updateRideAccessKey',
                    'ReturnToSingleRide',
                    'getRideAccessKey',
                    'exportTripToPDF',
                    'showModalWithParameters',
                    'fetchBillingProfile',
                    'approvePrepaidRideShare',
                    'showModal',
                    'restoreRideAccessLink',
                    ] ),
    // Load share info for postpaid users and for prepaid rides already paid.
    async loadShareInfo() {
      const rideIdFromUrl = this.$route.params.ride_key;
      if (this.isPrepaid && !this.isApprovedForSharing) {
        return;
      }
      // AWAITED, not fired alongside loadWhatsappInvite. getRideAccessKey hits
      // /get_assigned_ride_key_full_information, which CREATES the
      // RideAccessKeys row when a ride has none yet
      // (manageRidePrivacy/views.py rideKeyFullInformation). Run in parallel,
      // the invite request could reach the server before that row was
      // committed, so _eligible_key_or_error found no key and answered 409
      // no_access_key -- rendering "צרו קודם קישור הרשמה לטיול הזה" on the
      // FIRST visit to a ride, and working on every visit after, because by
      // then the row existed.
      try {
        await this.getRideAccessKey(rideIdFromUrl);
      } catch (error) {
        // Whatever broke here already costs the user the rest of this page; it
        // must not also suppress the invite panel, which reports its own errors.
      }
      // Same condition on purpose: asking for the invite state while sharing is
      // unpaid would only render a panel that must not be offered yet.
      this.loadWhatsappInvite();
      this.loadReminderSettings();
    },
    onPrepaidPrimaryClick() {
      if (this.approval.loading || (this.ridePriceBreakdown && this.ridePriceBreakdown.already_paid) || !this.canApproveSharing) {
        return;
      }
      if (this.businessBasicAgreementAccepted === true) {
        this.approveAndLoadShareInfo();
        return;
      }
      this.onContinueToTermsApprovalClick();
    },
    onContinueToTermsApprovalClick() {
      if (this.approval.loading || (this.ridePriceBreakdown && this.ridePriceBreakdown.already_paid) || !this.canApproveSharing) {
        return;
      }
      this.$store.commit('setBisnessBasicAgreementShareGateArmed', true);
      this.$store.commit('setBusinessBasicAgreementFlow', 'prepaid_share');
      this.showModal('businessBasicAgreement');
    },
    async saveRideAccessKeyDetailes() {
      const rideIdFromUrl = this.$route.params.ride_key;

      // Awaited, where this used to fire and navigate in the same breath: the
      // router tearing the page down could cancel the request mid-flight and
      // the manager would never learn their change had not been saved.
      try {
        await this.updateRideAccessKey( rideIdFromUrl );
      } catch ( error ) {
        this.reminderError = this.t( 'whatsappReminderError' );
        return;
      }

      // The reminder settings live behind a different endpoint, which is why
      // this panel used to carry its own Save button. Two Save buttons meant a
      // manager who changed the send hour and then pressed the page's main
      // Save lost that change silently. One button now saves both.
      if ( this.whatsappInviteAllowed ) {
        const saved = await this.saveReminderSettings();
        if ( !saved ) {
          // Stay put so the reason is readable instead of navigating away as
          // though it had worked.
          return;
        }
      }

      this.$router.push('/single_ride_with_map/' + rideIdFromUrl);
    },
    returnToRide() {
      const rideIdFromUrl = this.$route.params.ride_key;
      if (rideIdFromUrl) {
        this.$router.push('/single_ride_with_map/' + rideIdFromUrl);
      }

    },
    goToGetSingleAccessKey() {
      this.$router.push('/generate_one_ride_access_key/' + this.$route.params.ride_key);
    },

    openPdfStyleChooser() {
      this.$bvModal.show('pdfStyleModal');
    },

    openBrochurePhotoPicker() {
      this.$bvModal.hide('pdfStyleModal');
      this.$refs.brochurePhotoPicker.open();
    },

    async exportToPDF(style = 'itinerary', photos = null) {
      const rideIdFromUrl = this.$route.params.ride_key;

      this.$bvModal.hide('pdfStyleModal');

      if (!rideIdFromUrl) {
        this.showModalWithParameters({
          errorNumber: "ride_data_not_loaded",
          type: 'textConverter'
        });
        return;
      }

      // Show warning popup first
      this.showModalWithParameters({
        errorNumber: "pdf_generation_warning",
        type: 'textConverter'
      });

      // Start PDF generation immediately after warning
      this.isGeneratingPDF = true;

      await this.exportTripToPDF({
        rideId: rideIdFromUrl,
        tripTitle: this.rideInformationTitle || 'Trip',
        style,
        photos
      });

      this.isGeneratingPDF = false;
    },

    toggleShareRideVideo() {
      if (this.showShareRideVideo) {
        const ref = this.$refs.shareRideVideo;
        if (ref) {
          ref.pause();
          ref.currentTime = 0;
        }
        this.showShareRideVideo = false;
        return;
      }
      this.showShareRideVideo = true;
      this.shareRideTutorialVideo.loading = true;
      this.shareRideTutorialVideo.error = false;
      this.$nextTick(() => {
        const ref = this.$refs.shareRideVideo;
        if (ref && ref.load) ref.load();
      });
    },
    onShareRideVideoLoadStart() {
      this.shareRideTutorialVideo.loading = true;
    },
    onShareRideVideoLoaded() {
      this.shareRideTutorialVideo.loading = false;
    },
    onShareRideVideoError() {
      this.shareRideTutorialVideo.loading = false;
      this.shareRideTutorialVideo.error = true;
    },
    retryShareRideVideo() {
      this.shareRideTutorialVideo.error = false;
      this.shareRideTutorialVideo.loading = true;
      this.$nextTick(() => {
        const ref = this.$refs.shareRideVideo;
        if (ref && ref.load) ref.load();
      });
    },
    async onRestoreLink() {
      const rideIdFromUrl = this.$route.params.ride_key;
      this.showModal('loadingIcon');
      await this.restoreRideAccessLink(rideIdFromUrl);
    },
    copyLink() {
      const link = this.rideAccesskeySecretKey;
      navigator.clipboard
        .writeText(link)
        .then(() => {
          this.$bvToast.toast(this.t('editRideAccessCopySuccessMessage'), {
            title: this.t('editRideAccessCopySuccessTitle'),
            variant: 'success',
            solid: true,
          });
        })
        .catch((err) => {
          console.error('Failed to copy link: ', err);
          this.$bvToast.toast(this.t('editRideAccessCopyErrorMessage'), {
            title: this.t('editRideAccessCopyErrorTitle'),
            variant: 'danger',
            solid: true,
          });
        });
    },

    async approveAndLoadShareInfo() {
      const rideIdFromUrl = this.$route.params.ride_key;
      if (!rideIdFromUrl) {
        return;
      }
      const wasFirstShare = this.isFirstShareOnUs;
      await this.approvePrepaidRideShare(rideIdFromUrl);
      if (wasFirstShare) {
        this.showSaveCardBanner = true;
      }
      // created() deliberately skips this while sharing is unpaid, so the panel
      // needs its state fetching once the payment goes through -- otherwise the
      // manager has to reload the page to see the WhatsApp option at all.
      if (this.isApprovedForSharing) {
        await this.loadWhatsappInvite();
      }
    },
    openWhatsAppSupport() {
      const message = this.t('editRideAccessOpenWhatsAppSupportMessage');
      const url = `https://wa.me/972526867915?text=${encodeURIComponent(message)}`;
      window.open(url, '_blank');
    },
    openInsufficientBalancePopup() {
      const rideKey = this.$route.params.ride_key;
      const returnTo = `/ride_access_key_information/${rideKey}`;
      this.$router.push(`/billing/monthly_card_setup?returnTo=${encodeURIComponent(returnTo)}`);
    },
    async loadWhatsappInvite() {
      this.whatsappInviteError = '';
      try {
        const response = await whatsappInviteApi.getInvite( this.apptoken, this.$route.params.ride_key );
        this.whatsappInvite = response.data;
        if ( !response.data.invite_url && !response.data.can_generate ) {
          this.whatsappInviteError = this.t( 'whatsappInviteNotConfigured' );
        }
      } catch ( error ) {
        if ( error.response && error.response.data
             && error.response.data.error === 'ride_not_private' ) {
          this.whatsappRideNotPrivate = true;
          return;
        }
        this.whatsappInviteError = this.mapWhatsappInviteError( error );
      }
    },
    async generateWhatsappInvite() {
      this.whatsappInviteError = '';
      this.generatingInvite = true;
      try {
        const response = await whatsappInviteApi.generateInvite( this.apptoken, this.$route.params.ride_key );
        this.whatsappInvite = response.data;
        if ( !response.data.invite_url && !response.data.can_generate ) {
          this.whatsappInviteError = this.t( 'whatsappInviteNotConfigured' );
        }
      } catch ( error ) {
        this.whatsappInviteError = this.mapWhatsappInviteError( error );
      } finally {
        this.generatingInvite = false;
      }
    },
    mapWhatsappInviteError( error ) {
      const status = error.response && error.response.status;
      const errorCode = error.response && error.response.data && error.response.data.error;
      if ( status === 409 && errorCode === 'unsupported_key_action' ) {
        return this.t( 'whatsappInviteUnsupportedKeyAction' );
      }
      if ( status === 409 && errorCode === 'share_not_approved' ) {
        return this.t( 'whatsappInviteShareNotApproved' );
      }
      if ( status === 409 && errorCode === 'inactive_access_key' ) {
        return this.t( 'whatsappInviteInactiveKey' );
      }
      if ( status === 409 ) {
        return this.t( 'whatsappInviteNeedsKey' );
      }
      return this.t( 'whatsappInviteError' );
    },
    async copyWhatsappInvite() {
      try {
        await navigator.clipboard.writeText( this.whatsappInvite.invite_url );
        this.$bvToast.toast( this.t( 'whatsappInviteCopied' ), { variant: 'success', noCloseButton: true } );
      } catch ( error ) {
        this.$bvToast.toast( this.t( 'whatsappInviteCopyFailed' ), { variant: 'danger', noCloseButton: true } );
      }
    },
    zoneLabel( value ) {
      // Falls back to the raw IANA name for a zone that is not in the curated
      // list -- a manager can still hold one set earlier through the API, and
      // showing 'Europe/Rome' is better than showing nothing.
      let named = null;
      TIMEZONE_GROUPS.forEach( ( group ) => {
        const found = group.zones.find( ( zone ) => zone.value === value );
        if ( found ) {
          named = found;
        }
      } );
      const name = named ? ( this.isHebrew ? named.he : named.en ) : value;
      const offset = offsetLabel( value );
      return offset ? `${name}  ${offset}` : name;
    },
    async loadReminderSettings() {
      try {
        const response = await whatsappInviteApi.getReminderSettings(
          this.apptoken, this.$route.params.ride_key );
        // Merged, not replaced: an older backend that predates FIX 4 omits
        // template_available entirely, and a plain assignment would turn that
        // missing key into undefined -- falsy, and indistinguishable in the
        // template from a real "no template" answer.
        this.reminderSettings = { ...this.reminderSettings, ...response.data };
      } catch ( error ) {
        // The invite panel must keep working even if settings cannot be read.
      }
    },
    async saveReminderSettings() {
      // Returns whether it succeeded rather than announcing it: the page-level
      // save owns the outcome and navigates away on success, so a toast here
      // would flash and vanish.
      this.reminderError = '';
      try {
        const payload = {
          enabled: this.reminderSettings.enabled,
          hour: this.reminderSettings.hour,
          timezone: this.reminderSettings.timezone,
          daily_itinerary_enabled: this.reminderSettings.daily_itinerary_enabled,
        };
        const response = await whatsappInviteApi.saveReminderSettings(
          this.apptoken, this.$route.params.ride_key, payload );
        this.reminderSettings = { ...this.reminderSettings, ...response.data };
        return true;
      } catch ( error ) {
        const code = error.response && error.response.data && error.response.data.error;
        this.reminderError = code === 'invalid_timezone'
          ? this.t( 'whatsappReminderBadTimezone' )
          : this.t( 'whatsappReminderError' );
        return false;
      }
    },
  },
  async created() {
    const rideIdFromUrl = this.$route.params.ride_key;
    if (!rideIdFromUrl) {
      return;
    }
    // Step 1: ask backend for billing mode and (for prepaid) the ride price.
    await this.fetchBillingProfile(rideIdFromUrl);

    // Ride manager who has not signed the current agreement: the gate card shows
    // instead; loadShareInfo runs once they sign (mustSignUpdatedTerms watcher).
    if (this.mustSignUpdatedTerms) {
      return;
    }
    await this.loadShareInfo();
  },
};
</script>


<style scoped>
/* Bootstrap pins the dismiss "x" to the right; in Hebrew it would sit on the text. */
.save-card-banner[dir="rtl"] {
  padding-right: 1.25rem;
  padding-left: 4rem;
}
.save-card-banner[dir="rtl"] ::v-deep .close {
  right: auto;
  left: 0;
}
.setup-billing-btn {
  padding: 12px 16px !important;
  line-height: 1.3 !important;
  height: auto !important;
  box-shadow: 0 4px 12px rgba(40, 167, 69, 0.4) !important;
}

.setup-billing-btn__icon {
  margin-inline-end: 10px;
}

.setup-billing-btn__main {
  font-size: 16px;
  font-weight: 700;
}

.setup-billing-btn__sub {
  font-size: 12px;
  font-weight: 400;
  opacity: 0.9;
  margin-top: 2px;
}

custom-height {
  height: 50px; /* Adjust to desired height */
  line-height: 1.5; /* Ensures proper text alignment */
}

#checkbox-1 {
  display: flex;
  align-items: center;
}
#checkbox-2 {
  display: flex;
  align-items: center;
}

.form-control{
  background-color: white !important;
}
.dropdown-menu {
  margin-top: 6px;
  width: 100% !important;
}

.help-icon-btn {
  text-decoration: none !important;
  border: none !important;
  cursor: pointer;
}

.help-icon-btn:hover {
  opacity: 0.7;
}

.help-icon-btn:focus {
  box-shadow: none !important;
  outline: none !important;
}

#label-details::v-deep > div:not([role]),
#label-allow-sale-api::v-deep > div:not([role]) {
  margin-inline-end: 1rem;
}

[dir="rtl"] #label-details label,
[dir="rtl"] #label-allow-sale-api label {
  display: flex;
  align-items: center;
  flex-direction: row; /* text on the right, button on the left */
}

::v-deep .popover {
  max-width: 400px;
}

[dir="rtl"] ::v-deep .popover,
[dir="rtl"] ::v-deep .popover-body {
  text-align: right;
  direction: rtl;
  line-height: 1.6;
}

[dir="ltr"] ::v-deep .popover,
[dir="ltr"] ::v-deep .popover-body {
  text-align: left;
  direction: ltr;
  line-height: 1.6;
}

/* Share-ride video tutorial section */
.video-tutorials-section {
  margin-top: 0;
  margin-bottom: 24px;
  background-color: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  padding: 20px;
  direction: rtl;
  text-align: center;
}

[dir="ltr"] .video-tutorials-section {
  direction: ltr;
  text-align: center;
}

.video-tutorials-title {
  font-weight: 700;
  font-size: 20px;
  line-height: 28px;
  color: #0D2F29;
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.video-tutorials-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.video-tutorial-item {
  background-color: #fff;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.video-tutorial-card {
  cursor: pointer;
  padding: 16px;
  transition: background-color 0.2s ease;
  min-height: 50px;
  display: flex;
  align-items: center;
}

.video-tutorial-card:hover {
  background-color: #f0f0f0;
}

.video-tutorial-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  width: 100%;
}

.video-tutorials-section .video-tutorial-title {
  font-weight: 600;
  font-size: 16px;
  line-height: 22px;
  color: #0D2F29;
  margin: 0;
  flex: 1;
  text-align: center;
}

.video-tutorial-arrow {
  font-size: 12px;
  color: #07A0C3;
  transition: transform 0.3s ease;
  flex-shrink: 0;
}

.video-tutorial-arrow.is-open {
  transform: rotate(90deg);
}

.video-container-wrapper {
  padding: 0 16px 16px 16px;
}

.slide-down-enter-active,
.slide-down-leave-active {
  transition: all 0.3s ease;
  overflow: hidden;
}

.slide-down-enter,
.slide-down-leave-to {
  max-height: 0;
  opacity: 0;
}

.slide-down-enter-to,
.slide-down-leave {
  max-height: 1000px;
  opacity: 1;
}

.tutorial-video-container {
  position: relative;
  width: 100%;
  border-radius: 8px;
  overflow: hidden;
  background-color: #000;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.tutorial-video {
  width: 100%;
  height: auto;
  display: block;
  max-height: 60vh;
}

.video-loading,
.video-error {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 200px;
  padding: 2rem;
  color: #666;
  background-color: #f5f5f5;
  border-radius: 8px;
}

.video-loading p,
.video-error p {
  margin-top: 1rem;
  font-size: 14px;
  text-align: center;
}

.retry-button {
  margin-top: 1rem;
  padding: 8px 20px;
  background-color: #07A0C3;
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
}

.retry-button:hover {
  background-color: #0590b0;
}

.spinner-border {
  display: inline-block;
  width: 2rem;
  height: 2rem;
  vertical-align: text-bottom;
  border: 0.25em solid currentColor;
  border-right-color: transparent;
  border-radius: 50%;
  animation: spinner-border 0.75s linear infinite;
}

.spinner-border.text-primary {
  color: #07A0C3;
}

@keyframes spinner-border {
  to {
    transform: rotate(360deg);
  }
}

.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border-width: 0;
}

.whatsapp-invite-panel {
  margin-top: 16px;
  margin-bottom: 16px;
  padding: 16px;
  border: 1px solid #d1e7dd;
  border-radius: 8px;
  background-color: #f6fdf9;
}

.whatsapp-invite-title {
  font-size: 1.1rem;
  margin-bottom: 8px;
}

.whatsapp-invite-explain {
  margin-bottom: 12px;
}

.whatsapp-invite-billing-notice {
  margin-bottom: 12px;
  border-width: 2px;
}

.whatsapp-invite-billing-icon {
  margin-inline-end: 6px;
}

.whatsapp-invite-notice-title {
  font-weight: 700;
  margin-bottom: 8px;
}

.whatsapp-invite-notice-list {
  margin: 0;
  font-weight: 400;
  /* padding, not margin: the bullets must sit inside the alert on the reading
     side, and padding-inline-start flips with the RTL/LTR direction. */
  padding-inline-start: 20px;
  padding-inline-end: 0;
}

.whatsapp-invite-notice-list li {
  margin-bottom: 8px;
}

.whatsapp-invite-notice-list li:last-child {
  margin-bottom: 0;
}

.whatsapp-invite-copy {
  margin-top: 8px;
}

.whatsapp-invite-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  margin-top: 12px;
}

.whatsapp-invite-error {
  color: #b02a37;
  margin-top: 16px;
}

.whatsapp-reminder {
  margin-top: 16px;
  padding-top: 12px;
  border-top: 1px solid #d1e7dd;
}

.whatsapp-reminder-title {
  font-size: 16px;
  font-weight: 600;
  margin-bottom: 6px;
}

.whatsapp-reminder-explain {
  font-size: 14px;
  color: #5a6a63;
  margin-bottom: 10px;
}

.whatsapp-reminder-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
  font-size: 14px;
}

.whatsapp-reminder-row label {
  margin-bottom: 0;
  min-width: 120px;
}

/* Collapsed-by-default toggle for a preview, reusing the ▶ arrow idiom from
   the video-tutorial section near the top of this file. */
.whatsapp-reminder-preview-toggle {
  display: flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  font-size: 13px;
  font-weight: 600;
  color: #07A0C3;
  margin: 10px 0;
}

.whatsapp-reminder-preview-arrow {
  font-size: 12px;
  transition: transform 0.3s ease;
  flex-shrink: 0;
}

.whatsapp-reminder-preview-arrow.is-open {
  transform: rotate(90deg);
}

.whatsapp-reminder-preview {
  background-color: #ffffff;
  border: 1px solid #e6f2ec;
  border-radius: 8px;
  padding: 10px 12px;
  margin: 10px 0;
}

.whatsapp-reminder-preview-label {
  font-size: 13px;
  font-weight: 600;
  margin-bottom: 4px;
}

.whatsapp-reminder-preview-body {
  font-size: 15px;
  line-height: 1.5;
  white-space: pre-wrap;
  /* Explicit dir (bound to isHebrew in the template) plus isolate: without
     this, the \n in the sample text makes the browser treat each line as
     its own bidi paragraph and re-guess its direction from the first
     strong character on that line -- flipping a leading "1." to ".1" on
     lines that start with a number. isolate (not plaintext) keeps the
     block's own explicit direction instead of resolving per line. */
  unicode-bidi: isolate;
  /* Long, unbreakable map-link tokens must break at the container edge
     instead of overflowing or forcing a horizontal scrollbar. */
  overflow-wrap: anywhere;
}

.whatsapp-reminder-preview-note {
  font-size: 12px;
  color: #6c757d;
  margin-top: 6px;
}

.whatsapp-reminder-no-template {
  background-color: #fff8e6;
  border: 1px solid #f0d99a;
  border-radius: 8px;
  padding: 10px 12px;
  margin: 10px 0;
  font-size: 13px;
  color: #7a5c00;
}

.whatsapp-reminder-error {
  margin-inline-start: 10px;
  font-size: 13px;
  color: #b02a37;
}

/* Sits under the nightly-itinerary checkbox. Indented to the checkbox's label
   with a logical property so it follows the text in RTL as well as LTR, and
   sized in px because the app sets html { font-size: 10px } and 1rem would be
   10px, not 16px. */
.whatsapp-reminder-sub {
  margin-inline-start: 24px;
  margin-bottom: 10px;
  font-size: 13px;
  color: #5a6a63;
  /* The translation string embeds a blank line (\n\n) between its two
     paragraphs, the same idiom used by the preview-body strings elsewhere
     in this file. pre-line collapses the surrounding single newlines
     from wrapping but keeps that blank line as visible paragraph spacing,
     without a second <p> or v-html. */
  white-space: pre-line;
}

</style>